import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  List,
  ListItem,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import { Alert } from "@material-ui/lab";

import { useAlert } from "react-alert";

import useRunOneOffWebTrackerScan from "../mutations/useRunOneOffWebTrackerScan";
import { RunCrawlerRequest } from "../types";
import EmailsChips from "../EmailsChips";
import validateStartingURL from "../validateStartingURL";

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
  },
  cardContent: {
    padding: theme.spacing(2),
    position: "relative",
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  emailChip: {
    marginRight: theme.spacing(1),
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  formLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-100%) translateY(-50%)",
  },
}));

function CreateSiteScannerForm() {
  const alert = useAlert();
  const classes = useStyles();

  const runCrawlerRequestMutation = useRunOneOffWebTrackerScan(
    () => {
      alert.success("Scan started");
    },
    (errorMessage) => {
      alert.error(errorMessage);
    }
  );

  return (
    <Card className={classes.card}>
      <CardHeader title="Scan a Site" className={classes.cardHeader} />

      <Divider />

      <CardContent className={classes.cardContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body1">
              Enter a <strong>single full URL</strong> below to scan a site.
              Usually this would be the "home" page of the site, like{" "}
              <code>https://www.freshpaint.io/</code>. This tool will run two
              scans of the site:
            </Typography>
            <List>
              <ListItem>
                <Typography variant="body1">
                  1. A quick scan limited to the first 100 pages the tracker
                  auditor finds
                </Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body1">
                  2. A full scan of the site
                </Typography>
              </ListItem>
            </List>
            <Typography variant="body1">
              You should receive an email when each scan is complete. The email
              will be sent to the email address associated with the Freshpaint
              account you are logged in with.
            </Typography>

            <Alert severity="warning" style={{ marginTop: "1em" }}>
              <Typography variant="body1">
                Emails will be sent automatically once the scan completes, so{" "}
                <strong>
                  you will not be able to see or QA the report's contents
                </strong>{" "}
                before the email is sent to any additional recipients below.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </CardContent>

      <Divider />

      <CardActions className={classes.cardContent}>
        <CreateSiteScannerFormInternal
          loading={runCrawlerRequestMutation.isLoading}
          onSubmit={(startingURL, emails) => {
            if (runCrawlerRequestMutation.isLoading) {
              return;
            }

            const req: RunCrawlerRequest = {
              startingURL,
              emails,
            };

            runCrawlerRequestMutation.mutate(req);
          }}
        />
      </CardActions>
    </Card>
  );
}

type CreateSiteScannerFormInternalProps = {
  loading: boolean;
  onSubmit: (startingURL: string, emails: string[]) => void;
};

function CreateSiteScannerFormInternal({
  loading,
  onSubmit,
}: CreateSiteScannerFormInternalProps) {
  const classes = useStyles();
  const [startingUrl, setStartingUrl] = useState("");
  const [emails, setEmails] = useState<string[]>([]);

  const startingURLErrors = useMemo(() => {
    const errors = validateStartingURL(startingUrl);

    return errors.sort().join(", ");
  }, [startingUrl]);

  const onAddEmail = (emailToAdd: string) => {
    if (emailToAdd === "") return;

    setEmails(_.uniq([...emails, emailToAdd]));
  };

  const onRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const onClickSubmit = () => {
    if (startingURLErrors !== "" || emails.length === 0) {
      return;
    }

    onSubmit(startingUrl, _.uniq(emails));
  };

  return (
    <Grid item container xs={12} style={{ flexDirection: "row-reverse" }}>
      <FormControl fullWidth className={classes.formControl}>
        <FormLabel className={classes.formLabel}>
          <Typography variant="overline">Starting URL</Typography>
        </FormLabel>

        <TextField
          variant="outlined"
          placeholder={
            'A single URL to start scanning from, ie. "https://www.freshpaint.io/"'
          }
          type="url"
          disabled={loading}
          error={startingURLErrors !== ""}
          helperText={startingURLErrors}
          size="small"
          value={startingUrl}
          onChange={(e) => setStartingUrl(e.target.value)}
          required
        />
      </FormControl>

      <FormControl className={classes.formControl} fullWidth>
        <FormLabel className={classes.formLabel}>
          <Typography variant="overline">Emails to Send Reports To</Typography>
        </FormLabel>

        <EmailsChips
          disabled={loading}
          emails={emails}
          onAdd={onAddEmail}
          onDelete={onRemoveEmail}
        />
      </FormControl>

      <Grid item>
        <Button
          color="primary"
          disabled={loading || emails.length === 0 || startingURLErrors !== ""}
          variant="contained"
          onClick={() => {
            onClickSubmit();
          }}
        >
          Run Scan
        </Button>
      </Grid>
    </Grid>
  );
}

export default CreateSiteScannerForm;
